<template>
  <div class="register-page" :class="{ 'register-page--mobile': mobile }">
    <header class="register-topbar">
      <div class="register-brand">
        <BaseIcon icon="logo-angular" size="24" />
        <span class="register-brand-title">任吾行数智化协同平台</span>
      </div>
      <span class="register-topbar-tag">邀请注册</span>
    </header>

    <main ref="scrollBody" class="register-body" @touchmove.stop>
      <div class="register-card">
        <div v-if="!invcode" class="register-empty">
          <BaseIcon icon="md-alert" size="48" class="register-empty-icon register-empty-icon--warn" />
          <p class="register-empty-title">此页面为邀请注册页面</p>
          <p class="register-empty-desc">请勿直接访问，或联系管理员获取邀请链接</p>
        </div>

        <Spin v-else-if="loading" fix>
          <BaseLoading />
          <div class="register-loading-text">加载邀请信息…</div>
        </Spin>

        <div v-else-if="loadError" class="register-empty">
          <BaseIcon icon="md-close-circle" size="48" class="register-empty-icon register-empty-icon--error" />
          <p class="register-empty-title">邀请码无效</p>
          <p class="register-empty-desc">{{ loadError }}</p>
        </div>

        <template v-else-if="invInfo">
          <div class="register-invite">
            <p v-if="invInfo.message" class="register-message">{{ invInfo.message }}</p>
            <div class="register-expire" :class="{ expired: isExpired }">
              <BaseIcon icon="md-time" size="16" />
              <span v-if="isExpired">邀请已过期</span>
              <span v-else>有效期剩余：{{ countdownText }}</span>
            </div>
          </div>

          <Form
            v-if="!submitted"
            ref="form"
            :model="formData"
            :rules="formRules"
            :label-width="formLabelWidth"
            :label-position="formLabelPosition"
            class="register-form"
            @submit.native.prevent
          >
            <div class="register-section">
              <div class="register-section-head">
                <BaseIcon icon="md-lock" size="18" />
                <span>账号信息</span>
              </div>
              <FormItem label="用户名" prop="account">
                <Input
                  v-model="formData.account"
                  class="register-input"
                  placeholder="请输入用户名"
                  clearable
                  :disabled="isExpired || submitting"
                />
              </FormItem>
              <FormItem label="密码" prop="password">
                <Input
                  v-model="formData.password"
                  class="register-input"
                  type="password"
                  password
                  placeholder="至少8位，含数字和字母"
                  :disabled="isExpired || submitting"
                />
              </FormItem>
              <FormItem label="确认密码" prop="confirm_password">
                <Input
                  v-model="formData.confirm_password"
                  class="register-input"
                  type="password"
                  password
                  placeholder="请再次输入密码"
                  :disabled="isExpired || submitting"
                />
              </FormItem>
            </div>

            <div class="register-section">
              <div class="register-section-head">
                <BaseIcon icon="md-contact" size="18" />
                <span>个人信息</span>
              </div>
              <FormItem label="姓名" prop="name">
                <Input
                  v-model="formData.name"
                  class="register-input"
                  placeholder="请输入姓名"
                  clearable
                  :disabled="isExpired || submitting"
                />
              </FormItem>
              <FormItem label="手机号" prop="phone">
                <Input
                  v-model="formData.phone"
                  class="register-input"
                  maxlength="11"
                  placeholder="请输入11位手机号"
                  clearable
                  :disabled="isExpired || submitting"
                />
              </FormItem>
              <FormItem label="短信验证码" prop="vcode" class="register-vcode-item">
                <div class="register-vcode-row">
                  <Input
                    v-model="formData.vcode"
                    class="register-input register-vcode-input"
                    placeholder="请输入6位验证码"
                    :maxlength="6"
                    clearable
                    :disabled="isExpired || submitting"
                    @on-enter="handleSubmit"
                  />
                  <Button
                    type="default"
                    class="register-vcode-btn"
                    :disabled="isExpired || submitting || vcodeCooldown > 0"
                    :loading="sendingVcode"
                    @click="sendPhoneVcode"
                  >
                    {{ vcodeBtnText }}
                  </Button>
                </div>
              </FormItem>
              <FormItem label="备注" prop="note">
                <Input
                  v-model="formData.note"
                  class="register-input register-note-input"
                  type="textarea"
                  :rows="2"
                  placeholder="选填"
                  :disabled="isExpired || submitting"
                />
              </FormItem>
            </div>

            <FormItem class="register-submit-item" :label-width="0">
              <Button
                type="primary"
                long
                class="register-submit-btn"
                :loading="submitting"
                :disabled="isExpired"
                @click="handleSubmit"
              >
                提交注册
              </Button>
            </FormItem>
          </Form>

          <div v-else class="register-success">
            <BaseIcon icon="md-checkmark-circle" size="56" class="register-success-icon" />
            <p class="register-success-title">{{ successTitle }}</p>
            <p class="register-success-desc">{{ successDesc }}</p>
            <Button
              v-if="!invInfo.need_approval"
              type="primary"
              long
              class="register-submit-btn"
              @click="goLogin"
            >
              前往登录
            </Button>
          </div>
        </template>
      </div>
    </main>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/Icon';
import UTIL from '@/utils';

const PHONE_REG = /^1[3-9]\d{9}$/;
const PASSWORD_REG = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

function isValidPhone(phone) {
  return PHONE_REG.test(String(phone || '').trim());
}

function isValidPassword(password) {
  return PASSWORD_REG.test(String(password || ''));
}

export default {
  name: 'ToolsRegister',
  metaInfo: {
    title: '邀请注册 - 任吾行数智化协同平台',
  },
  components: { BaseIcon },
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请再次输入密码'));
      } else if (value !== this.formData.password) {
        callback(new Error('两次输入的密码不一致'));
      } else {
        callback();
      }
    };

    const validatePhone = (rule, value, callback) => {
      if (!value || !String(value).trim()) {
        callback(new Error('请输入手机号'));
      } else if (!isValidPhone(value)) {
        callback(new Error('请输入正确的11位手机号'));
      } else {
        callback();
      }
    };

    const validatePassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入密码'));
      } else if (!isValidPassword(value)) {
        callback(new Error('密码至少8位，且包含数字和字母'));
      } else {
        callback();
      }
    };

    const validateVcode = (rule, value, callback) => {
      const code = String(value || '').trim();
      if (!code) {
        callback(new Error('请输入短信验证码'));
      } else if (!/^\d{6}$/.test(code)) {
        callback(new Error('验证码为6位数字'));
      } else {
        callback();
      }
    };

    return {
      loading: false,
      submitting: false,
      sendingVcode: false,
      submitted: false,
      loadError: '',
      invInfo: null,
      countdownText: '',
      isExpired: false,
      countdownTimer: null,
      vcodeCooldown: 0,
      vcodeCooldownTimer: null,
      vcodeSentOnce: false,
      formData: {
        account: '',
        name: '',
        phone: '',
        vcode: '',
        password: '',
        confirm_password: '',
        note: '',
      },
      formRules: {
        account: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
        name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        phone: [{ required: true, validator: validatePhone, trigger: 'blur' }],
        vcode: [{ required: true, validator: validateVcode, trigger: 'blur' }],
        password: [{ required: true, validator: validatePassword, trigger: 'blur' }],
        confirm_password: [{ required: true, validator: validateConfirmPassword, trigger: 'blur' }],
      },
    };
  },
  computed: {
    mobile() {
      return this.$store.getters['session/mobile'];
    },
    formLabelWidth() {
      return this.mobile ? 0 : 96;
    },
    formLabelPosition() {
      return this.mobile ? 'top' : 'right';
    },
    invcode() {
      const code = this.$route.query.invcode;
      return code != null && String(code).trim() !== '' ? String(code).trim() : '';
    },
    successTitle() {
      if (!this.invInfo) return '注册成功';
      return this.invInfo.need_approval ? '注册已提交' : '注册成功';
    },
    successDesc() {
      if (!this.invInfo) return '';
      return this.invInfo.need_approval
        ? '已注册成功，等待管理员审核'
        : '您的账号已创建，是否前往登录？';
    },
    vcodeBtnText() {
      if (this.vcodeCooldown > 0) {
        return `已发送(${this.vcodeCooldown}s)`;
      }
      return this.vcodeSentOnce ? '重新发送' : '获取验证码';
    },
  },
  watch: {
    invcode: {
      handler(code) {
        this.resetState();
        if (code) {
          this.fetchInvcode();
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.clearCountdown();
    this.clearVcodeCooldown();
    this.unlockPageScroll();
  },
  mounted() {
    this.lockPageScroll();
  },
  methods: {
    lockPageScroll() {
      this._prevHtmlOverflow = document.documentElement.style.overflow;
      this._prevBodyOverflow = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    },
    unlockPageScroll() {
      document.documentElement.style.overflow = this._prevHtmlOverflow || '';
      document.body.style.overflow = this._prevBodyOverflow || '';
    },
    resetState() {
      this.clearCountdown();
      this.clearVcodeCooldown();
      this.loading = false;
      this.submitting = false;
      this.sendingVcode = false;
      this.submitted = false;
      this.loadError = '';
      this.invInfo = null;
      this.countdownText = '';
      this.isExpired = false;
      this.vcodeSentOnce = false;
      this.formData = {
        account: '',
        name: '',
        phone: '',
        vcode: '',
        password: '',
        confirm_password: '',
        note: '',
      };
    },
    clearVcodeCooldown() {
      if (this.vcodeCooldownTimer) {
        clearInterval(this.vcodeCooldownTimer);
        this.vcodeCooldownTimer = null;
      }
      this.vcodeCooldown = 0;
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    fetchInvcode() {
      this.loading = true;
      this.loadError = '';
      this.$api.get(`public/invcodes/${encodeURIComponent(this.invcode)}`)
        .then((res) => {
          this.invInfo = res.data.data || null;
          if (!this.invInfo) {
            this.loadError = '邀请信息不存在';
            return;
          }
          this.startCountdown(this.invInfo.expired_at);
        })
        .catch((err) => {
          this.loadError = typeof err === 'string' ? err : '加载邀请信息失败';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    startCountdown(expiredAt) {
      this.clearCountdown();
      if (!expiredAt) {
        this.countdownText = '长期有效';
        return;
      }

      const update = () => {
        const diff = new Date(expiredAt).getTime() - Date.now();
        if (diff <= 0) {
          this.isExpired = true;
          this.countdownText = '00:00:00';
          this.clearCountdown();
          return;
        }
        this.isExpired = false;
        const totalSec = Math.floor(diff / 1000);
        const days = Math.floor(totalSec / 86400);
        const hours = Math.floor((totalSec % 86400) / 3600);
        const minutes = Math.floor((totalSec % 3600) / 60);
        const seconds = totalSec % 60;
        const pad = (n) => String(n).padStart(2, '0');
        if (days > 0) {
          this.countdownText = `${days} 天 ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        } else {
          this.countdownText = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        }
      };

      update();
      this.countdownTimer = setInterval(update, 1000);
    },
    sendPhoneVcode() {
      const phone = this.formData.phone.trim();
      if (!phone) {
        this.Error('请输入手机号');
        return;
      }
      if (!isValidPhone(phone)) {
        this.Error('请输入正确的11位手机号');
        return;
      }

      this.sendingVcode = true;
      this.$api.post('sessions/phone-vcode', { phone })
        .then(() => {
          this.vcodeSentOnce = true;
          this.Success('验证码已发送');
          this.clearVcodeCooldown();
          this.vcodeCooldown = 60;
          this.vcodeCooldownTimer = setInterval(() => {
            this.vcodeCooldown -= 1;
            if (this.vcodeCooldown <= 0) {
              this.clearVcodeCooldown();
            }
          }, 1000);
        })
        .catch((err) => {
          this.Error(err);
        })
        .finally(() => {
          this.sendingVcode = false;
        });
    },
    handleSubmit() {
      if (this.isExpired || !this.invInfo) return;

      this.$refs.form.validate((valid) => {
        if (!valid) return;

        this.submitting = true;
        const payload = {
          phone: this.formData.phone.trim(),
          vcode: this.formData.vcode.trim(),
          account: this.formData.account.trim(),
          name: this.formData.name.trim(),
          password: UTIL.MD5(this.formData.password),
          inv_code: this.invcode,
          note: this.formData.note.trim(),
        };

        this.$api.post('sessions/register', payload)
          .then(() => {
            this.submitted = true;
            if (this.invInfo.need_approval) {
              this.$Modal.success({
                title: '注册已提交',
                content: '已注册成功，等待管理员审核',
              });
            } else {
              this.Confirm('注册成功，是否前往登录？', () => {
                this.goLogin();
              });
            }
          })
          .catch((err) => {
            this.Error(err);
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    goLogin() {
      this.RouteTo('/');
    },
  },
};
</script>

<style lang="less" scoped>
.register-page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  height: 100dvh;
  max-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--bg2);
  color: var(--text2);
  box-sizing: border-box;
  overflow: hidden;
  max-width: 100vw;
}

.register-topbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px;
  padding-top: max(10px, env(safe-area-inset-top));
  padding-left: max(16px, env(safe-area-inset-left));
  padding-right: max(16px, env(safe-area-inset-right));
  background: var(--bg1);
  color: var(--text1);
  border-bottom: 1px solid var(--border);
}

.register-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.register-brand-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.register-topbar-tag {
  flex-shrink: 0;
  font-size: 13px;
  color: var(--subtext1);
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.06);
}

.register-body {
  flex: 1;
  min-height: 0;
  display: flex;
  justify-content: center;
  padding: 16px 12px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
}

.register-card {
  width: 100%;
  max-width: 520px;
  align-self: flex-start;
  padding: 18px 16px;
  border-radius: 10px;
  background: var(--bg3);
  color: var(--text2);
  border: 1px solid var(--border);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow-x: hidden;
  box-sizing: border-box;
}

.register-loading-text {
  margin-top: 8px;
  color: var(--subtext2);
  font-size: 14px;
}

.register-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 12px;
}

.register-empty-icon {
  margin-bottom: 16px;

  &--warn {
    color: var(--warning);
  }

  &--error {
    color: var(--error);
  }
}

.register-empty-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text2);
  margin: 0 0 8px;
}

.register-empty-desc {
  font-size: 14px;
  color: var(--subtext2);
  margin: 0;
  line-height: 1.6;
}

.register-invite {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--bg2);
  border: 1px solid var(--border);
}

.register-message {
  font-size: 13px;
  color: var(--text2);
  margin: 0 0 8px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.register-expire {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
  color: var(--primary);
  line-height: 1.5;

  &.expired {
    color: var(--error);
  }
}

.register-section {
  margin-bottom: 6px;
  padding: 12px;
  border-radius: 8px;
  background: var(--bg2);
  border: 1px solid var(--border);
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;

  & + & {
    margin-top: 8px;
  }
}

.register-section-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border);
  font-size: 14px;
  font-weight: 600;
  color: var(--text2);
}

.register-form {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  /deep/ .ivu-form-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 20px;
    max-width: 100%;
  }

  /deep/ .ivu-form-item:last-child {
    margin-bottom: 0;
  }

  /deep/ .ivu-form-item-label {
    flex-shrink: 0;
    color: var(--text2);
    font-size: 13px;
    padding: 8px 10px 8px 0;
  }

  /deep/ .ivu-form-item-content {
    flex: 1;
    min-width: 0;
    margin-left: 0 !important;
  }

  /deep/ .ivu-form-item-error-tip {
    position: static;
    display: block;
    width: 100%;
    max-width: 100%;
    margin-top: 4px;
    padding-top: 0;
    font-size: 12px;
    line-height: 1.5;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
}

.register-input {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  /deep/ .ivu-input-wrapper {
    width: 100%;
    max-width: 100%;
  }

  /deep/ .ivu-input {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    height: 36px;
    line-height: 36px;
    border-radius: 6px;
    border-color: var(--border);
    background: var(--bg3);
    color: var(--text2);
    font-size: 14px;
    transition: border-color 0.2s, box-shadow 0.2s;

    &::placeholder {
      color: var(--subtext2);
    }

    &:hover {
      border-color: var(--hover);
    }

    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(51, 102, 153, 0.12);
    }

    &:disabled {
      background: var(--bg2);
      color: var(--subtext2);
    }
  }

  /deep/ .ivu-input-prefix {
    color: var(--subtext2);
  }
}

.register-note-input {
  /deep/ textarea.ivu-input {
    height: auto;
    min-height: 56px;
    line-height: 1.5;
    padding: 8px 10px;
    resize: vertical;
  }
}

.register-vcode-item {
  /deep/ .ivu-form-item-content {
    line-height: normal;
    min-width: 0;
  }
}

.register-vcode-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.register-vcode-input {
  flex: 1 1 auto;
  min-width: 0;
  max-width: calc(100% - 104px);
}

.register-vcode-btn {
  flex: 0 0 96px;
  width: 96px;
  min-width: 96px;
  max-width: 96px;
  height: 36px;
  padding: 0 4px;
  border-radius: 6px;
  border-color: var(--border);
  background: var(--bg3);
  color: var(--primary);
  font-size: 12px;
  white-space: nowrap;

  &:hover:not([disabled]) {
    color: var(--primary);
    border-color: var(--primary);
    background: var(--bg2);
  }

  &[disabled] {
    color: var(--subtext2);
    background: var(--bg2);
  }
}

.register-submit-item {
  margin-top: 12px;
  margin-bottom: 0;

  /deep/ .ivu-form-item-content {
    margin-left: 0 !important;
  }
}

.register-submit-btn {
  min-height: 40px;
  font-size: 15px;
  border-radius: 6px;
  background: var(--primary);
  border-color: var(--primary);

  &:hover:not([disabled]) {
    filter: brightness(1.08);
  }
}

.register-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 12px 8px;
}

.register-success-icon {
  color: var(--success);
}

.register-success-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text2);
  margin: 16px 0 8px;
}

.register-success-desc {
  font-size: 14px;
  color: var(--subtext2);
  margin: 0;
  line-height: 1.6;
}

/* 平板 */
@media (max-width: 768px) {
  .register-body {
    padding: 12px 10px;
    align-items: stretch;
  }

  .register-card {
    max-width: none;
    padding: 14px 12px;
  }

  .register-brand-title {
    font-size: 16px;
  }

  .register-input /deep/ .ivu-input {
    height: 40px;
    line-height: 40px;
    font-size: 16px;
  }

  .register-vcode-btn {
    height: 40px;
    min-width: 96px;
  }
}

/* 手机 */
.register-page--mobile {
  .register-topbar {
    flex-wrap: wrap;
    padding: 12px;
    padding-top: max(12px, env(safe-area-inset-top));
  }

  .register-brand-title {
    font-size: 15px;
    white-space: normal;
    line-height: 1.35;
  }

  .register-body {
    padding: 12px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }

  .register-card {
    padding: 12px 10px;
    border-radius: 8px;
  }

  .register-section {
    padding: 10px;
  }

  .register-section-head {
    font-size: 13px;
    margin-bottom: 6px;
    padding-bottom: 5px;
  }

  .register-form /deep/ .ivu-form-item {
    flex-direction: column;
    margin-bottom: 18px;
  }

  .register-form /deep/ .ivu-form-item-label {
    width: 100% !important;
    text-align: left;
    padding-bottom: 6px;
    line-height: 1.4;
  }

  .register-form /deep/ .ivu-form-item-content {
    width: 100%;
    margin-left: 0 !important;
  }

  .register-vcode-row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .register-vcode-input {
    max-width: 100%;
  }

  .register-vcode-btn {
    flex: none;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    min-height: 40px;
    height: auto;
    padding: 8px 10px;
    font-size: 13px;
  }

  .register-submit-btn {
    width: 100%;
  }
}

@media (max-width: 380px) {
  .register-topbar-tag {
    font-size: 12px;
    padding: 3px 8px;
  }

  .register-brand-title {
    font-size: 14px;
  }
}

/* 横屏小屏 */
@media (max-height: 500px) and (orientation: landscape) {
  .register-body {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .register-card {
    padding: 14px 16px;
  }

  .register-section {
    padding: 12px;
  }

  .register-form /deep/ .ivu-form-item {
    margin-bottom: 10px;
  }
}
</style>
